import type { Token, Prices, FIFOResults } from '../types';
import { TokenPriceHistoryService } from './tokenPriceHistory';

/**
 * Risk Metrics Service
 * Calcule les métriques de risque du portfolio
 */

export interface TokenRiskMetrics {
  tokenId: string;
  displayName: string;
  /**
   * Annualised volatility in percent. Best estimate available given the
   * cached data: standard deviation of daily log returns over the lookback
   * window times sqrt(365). Falls back to a coarse `|change24h| * sqrt(365)`
   * heuristic when no price history is cached locally — flagged via the
   * `volatilitySource` field so the UI can downgrade confidence.
   */
  volatility: number;
  /**
   * Where `volatility` came from. 'rolling' is the proper estimator, 'heuristic'
   * is the legacy 24h-based approximation used as a fallback.
   */
  volatilitySource: 'rolling' | 'heuristic';
  exposure: number; // % du portfolio
  value: number; // Valeur en $
  weight: number; // Poids dans le portfolio (0-1)
}

export interface PortfolioRiskMetrics {
  totalValue: number;
  tokens: TokenRiskMetrics[];
  diversificationScore: number; // 0-100, plus c'est élevé, plus c'est diversifié
  weightedVolatility: number; // Volatilité moyenne pondérée du portfolio
  largestExposure: number; // % du plus gros holding
  numberOfHoldings: number;
  herfindahlIndex: number; // Index de concentration (0-1, plus c'est proche de 0, plus c'est diversifié)
}

/**
 * Lookback window for rolling volatility (calendar days). 30d is the
 * standard for portfolio reporting — long enough to smooth idiosyncratic
 * spikes, short enough to react to regime changes.
 */
const VOLATILITY_LOOKBACK_DAYS = 30;

/**
 * Trading days per year. Crypto markets are 24/7 so we use 365 here, not
 * the equity-market 252.
 */
const ANNUALISATION_FACTOR_DAYS = 365;

/**
 * Compute annualised volatility (in percent) from a cached daily price
 * history. Returns `null` when there is not enough data — at least 5
 * observations are required for the standard deviation to be meaningful.
 *
 * Implementation: sort prices by timestamp, compute daily log returns,
 * sample standard deviation, scale by sqrt(365).
 */
function computeRollingVolatility(priceHistory: Record<number, number>): number | null {
  const entries = Object.entries(priceHistory)
    .map(([ts, price]) => [Number(ts), price] as const)
    .sort((a, b) => a[0] - b[0]);

  if (entries.length < 5) return null;

  const logReturns: number[] = [];
  for (let i = 1; i < entries.length; i++) {
    const prev = entries[i - 1][1];
    const cur = entries[i][1];
    if (prev > 0 && cur > 0) {
      logReturns.push(Math.log(cur / prev));
    }
  }

  if (logReturns.length < 5) return null;

  const mean = logReturns.reduce((s, r) => s + r, 0) / logReturns.length;
  const variance =
    logReturns.reduce((s, r) => s + (r - mean) ** 2, 0) / (logReturns.length - 1);
  const dailyStdDev = Math.sqrt(variance);

  // Convert to percent and annualise.
  return dailyStdDev * Math.sqrt(ANNUALISATION_FACTOR_DAYS) * 100;
}

/**
 * Calcule les métriques de risque pour chaque token.
 *
 * Volatility: prefers a true 30-day rolling standard deviation of log
 * returns (read synchronously from the price-history cache populated by
 * the analytics pages). Falls back to the legacy `|change24h| * sqrt(365)`
 * heuristic when no cached history exists yet — typically right after
 * signup before the user has visited any analytics page.
 */
export function calculateTokenRiskMetrics(
  tokens: Token[],
  prices: Prices,
  fifoResults: FIFOResults
): TokenRiskMetrics[] {
  const metrics: TokenRiskMetrics[] = [];
  let totalValue = 0;

  // Calcul de la valeur totale
  tokens.forEach(token => {
    const fifo = fifoResults[token.id] || {};
    const currentPrice = prices[token.id]?.usd || 0;
    const qty = fifo.remainingQty || 0;
    const value = qty * currentPrice;
    totalValue += value;
  });

  // Calcul des métriques par token
  tokens.forEach(token => {
    const fifo = fifoResults[token.id] || {};
    const priceData = prices[token.id];
    const currentPrice = priceData?.usd || 0;
    const change24h = priceData?.usd_24h_change || 0;
    const qty = fifo.remainingQty || 0;
    const value = qty * currentPrice;
    const weight = totalValue > 0 ? value / totalValue : 0;
    const exposure = weight * 100;

    // Try to compute proper rolling volatility from the cached price
    // history. Pulls from localStorage synchronously via the cache reader,
    // so this stays a sync function and the existing call sites do not
    // need to be migrated to async.
    let volatility: number;
    let volatilitySource: 'rolling' | 'heuristic';
    const cachedHistory = TokenPriceHistoryService.getCache(token.id, VOLATILITY_LOOKBACK_DAYS);
    const rolling = cachedHistory ? computeRollingVolatility(cachedHistory) : null;
    if (rolling != null) {
      volatility = rolling;
      volatilitySource = 'rolling';
    } else {
      // Fallback: annualise |change_24h| as a coarse approximation. This
      // produces nonsensical numbers on volatile days (e.g. a 10% daily
      // move maps to 191% annualised) but it's still better than nothing
      // before the cache is warm.
      volatility = Math.abs(change24h) * Math.sqrt(ANNUALISATION_FACTOR_DAYS);
      volatilitySource = 'heuristic';
    }

    metrics.push({
      tokenId: token.id,
      displayName: token.displayName || token.id,
      volatility,
      volatilitySource,
      exposure,
      value,
      weight
    });
  });

  return metrics.sort((a, b) => b.value - a.value);
}

/**
 * Calcule les métriques de risque du portfolio
 */
export function calculatePortfolioRiskMetrics(
  tokens: Token[],
  prices: Prices,
  fifoResults: FIFOResults
): PortfolioRiskMetrics {
  const tokenMetrics = calculateTokenRiskMetrics(tokens, prices, fifoResults);
  const totalValue = tokenMetrics.reduce((sum, t) => sum + t.value, 0);

  // Volatilité moyenne pondérée
  const weightedVolatility = tokenMetrics.reduce(
    (sum, t) => sum + t.volatility * t.weight,
    0
  );

  // Plus gros holding
  const largestExposure = tokenMetrics.length > 0 ? tokenMetrics[0].exposure : 0;

  // Herfindahl Index (mesure de concentration)
  // HHI = Σ(weight_i)^2
  // 1.0 = concentration totale (1 seul asset)
  // 1/n = diversification parfaite (n assets de poids égal)
  const herfindahlIndex = tokenMetrics.reduce(
    (sum, t) => sum + Math.pow(t.weight, 2),
    0
  );

  // Score de diversification (0-100)
  // Basé sur l'inverse du Herfindahl Index, normalisé
  // 100 = parfaitement diversifié, 0 = concentré
  const maxHHI = 1.0; // 1 seul asset
  const minHHI = tokens.length > 0 ? 1 / tokens.length : 1; // Diversification parfaite
  const diversificationScore = tokens.length > 0
    ? ((maxHHI - herfindahlIndex) / (maxHHI - minHHI)) * 100
    : 0;

  return {
    totalValue,
    tokens: tokenMetrics,
    diversificationScore: Math.max(0, Math.min(100, diversificationScore)),
    weightedVolatility,
    largestExposure,
    numberOfHoldings: tokens.length,
    herfindahlIndex
  };
}

/**
 * Obtient un niveau de risque basé sur la volatilité
 */
export function getVolatilityLevel(volatility: number): {
  level: 'low' | 'medium' | 'high' | 'extreme';
  label: string;
  color: string;
} {
  if (volatility < 20) {
    return { level: 'low', label: 'Faible', color: 'green' };
  } else if (volatility < 50) {
    return { level: 'medium', label: 'Modérée', color: 'blue' };
  } else if (volatility < 100) {
    return { level: 'high', label: 'Élevée', color: 'orange' };
  } else {
    return { level: 'extreme', label: 'Extrême', color: 'red' };
  }
}

/**
 * Obtient un score de diversification sous forme de texte
 */
export function getDiversificationLevel(score: number): {
  level: 'poor' | 'fair' | 'good' | 'excellent';
  label: string;
  color: string;
} {
  if (score < 25) {
    return { level: 'poor', label: 'Faible', color: 'red' };
  } else if (score < 50) {
    return { level: 'fair', label: 'Moyenne', color: 'orange' };
  } else if (score < 75) {
    return { level: 'good', label: 'Bonne', color: 'blue' };
  } else {
    return { level: 'excellent', label: 'Excellente', color: 'green' };
  }
}

export const RiskMetricsService = {
  calculateTokenRiskMetrics,
  calculatePortfolioRiskMetrics,
  getVolatilityLevel,
  getDiversificationLevel
};
